<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

// Set to false to restore the sign up guide page
const comingSoon = ref(true)

const { t } = useI18n()

const steps = computed(() => [
  {
    number: '01',
    title: t('SignUpGuide.Step1Title'),
    description: t('SignUpGuide.Step1Desc'),
    iconBg: 'bg-[#0e4d5c]',
    iconColor: 'text-[#22d3ee]',
  },
  {
    number: '02',
    title: t('SignUpGuide.Step2Title'),
    description: t('SignUpGuide.Step2Desc'),
    iconBg: 'bg-[#3b1f6e]',
    iconColor: 'text-[#a78bfa]',
  },
  {
    number: '03',
    title: t('SignUpGuide.Step3Title'),
    description: t('SignUpGuide.Step3Desc'),
    iconBg: 'bg-[#0e4d3a]',
    iconColor: 'text-[#34d399]',
  },
  {
    number: '04',
    title: t('SignUpGuide.Step4Title'),
    description: t('SignUpGuide.Step4Desc'),
    iconBg: 'bg-[#0e4d5c]',
    iconColor: 'text-[#22d3ee]',
  },
])
</script>

<template>
  <div class="relative w-full bg-[#0d0f14] text-white px-4 mt-[60px] pt-[20px] lg:pt-[30px] pb-[80px]">
    <!-- ── Coming Soon Overlay (set comingSoon = ref(false) to restore) ── -->
    <div v-if="comingSoon" class="relative z-10 flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
      <!-- Glow blobs -->
      <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div class="absolute top-[10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-[#a855f7] opacity-[0.15] blur-[140px]"></div>
        <div class="absolute bottom-[10%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#00e5ff] opacity-[0.12] blur-[140px]"></div>
      </div>
      <div class="relative z-10 flex flex-col items-center gap-6">
        <div class="w-24 h-24 rounded-full bg-gradient-to-br from-[#7c3aed]/30 to-[#00e5ff]/20 border border-[#00e5ff]/30 flex items-center justify-center shadow-[0_0_40px_rgba(0,229,255,0.2)]">
          <svg class="w-12 h-12 text-[#00e5ff]" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
        </div>
        <h1 class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-widest uppercase bg-gradient-to-r from-[#c6f605] via-[#00e5ff] to-[#a855f7] bg-clip-text text-transparent leading-tight">
          {{ $t('Common.ComingSoon') }}
        </h1>
        <p class="text-white/50 text-lg sm:text-xl font-semibold max-w-md">
          {{ $t('Common.WillBeAvailableSoon') }}
        </p>
        <div class="w-32 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#00e5ff] to-transparent"></div>
        <p class="text-white/30 text-sm">{{ $t('Sidebar.Sign_Up_Guide') }}</p>
      </div>
    </div>

    <!-- ── Existing Content (hidden while comingSoon is true) ── -->
    <div v-else class="w-full">
      <!-- Color blobs -->
      <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div class="absolute top-[5%] right-[10%] w-[350px] h-[350px] rounded-full bg-[#22d3ee] opacity-[0.07] blur-[120px]"></div>
        <div class="absolute top-[20%] left-[-5%] w-[400px] h-[400px] rounded-full bg-[#7c3aed] opacity-[0.09] blur-[130px]"></div>
        <div class="absolute top-[45%] right-[-4%] w-[380px] h-[380px] rounded-full bg-[#a855f7] opacity-[0.08] blur-[140px]"></div>
        <div class="absolute top-[60%] left-[20%] w-[300px] h-[300px] rounded-full bg-[#4f8ef5] opacity-[0.07] blur-[110px]"></div>
        <div class="absolute top-[80%] right-[8%] w-[360px] h-[360px] rounded-full bg-[#7c3aed] opacity-[0.06] blur-[120px]"></div>
      </div>

      <div class="relative z-10 max-w-[1100px] mx-auto flex flex-col items-center gap-10">

        <!-- Header -->
        <div class="flex flex-col items-center gap-4 text-center pt-4">
          <h1 class="text-4xl sm:text-6xl font-black leading-tight">
            {{ $t('SignUpGuide.Header1') }} <span class="text-[#22d3ee]">{{ $t('SignUpGuide.Header2') }}</span>
          </h1>
          <p class="text-gray-400 text-sm sm:text-base max-w-[520px]">
            {{ $t('SignUpGuide.Description') }}
          </p>
        </div>

        <!-- Step Cards Grid -->
        <div class="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="bg-[#13161e] border border-[#1e2535] rounded-2xl p-6 flex flex-col gap-4"
          >
            <!-- Number + Icon row -->
            <div class="flex items-start justify-between">
              <span class="text-3xl sm:text-4xl font-black" style="background: linear-gradient(135deg, #4f8ef5, #7c3aed); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
                {{ step.number }}
              </span>
              <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', step.iconBg]">
                <!-- Globe icon (step 1) -->
                <svg v-if="i === 0" xmlns="http://www.w3.org/2000/svg" :class="['w-5 h-5', step.iconColor]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                <!-- Person add icon (step 2) -->
                <svg v-else-if="i === 1" xmlns="http://www.w3.org/2000/svg" :class="['w-5 h-5', step.iconColor]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="16" y1="11" x2="22" y2="11"/>
                </svg>
                <!-- Gift card icon (step 3) -->
                <svg v-else-if="i === 2" xmlns="http://www.w3.org/2000/svg" :class="['w-5 h-5', step.iconColor]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <rect x="2" y="9" width="20" height="13" rx="2"/>
                  <path stroke-linecap="round" d="M2 13h20"/>
                  <path stroke-linecap="round" d="M12 9V22"/>
                  <path stroke-linecap="round" d="M8 9a2 2 0 0 1 4-2 2 2 0 0 1 4 2"/>
                </svg>
                <!-- Link icon (step 4) -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" :class="['w-5 h-5', step.iconColor]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 0 0-5.656 0l-4 4a4 4 0 1 0 5.656 5.656l1.102-1.101"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10.172 13.828a4 4 0 0 0 5.656 0l4-4a4 4 0 1 0-5.656-5.656L13.07 5.27"/>
                </svg>
              </div>
            </div>

            <!-- Title + Description -->
            <div class="flex flex-col gap-2">
              <p class="text-white font-semibold text-xl sm:text-2xl tracking-wide">{{ step.title }}</p>
              <p class="text-gray-400 text-xs sm:text-sm leading-relaxed">{{ step.description }}</p>
            </div>
          </div>
        </div>

        <!-- CTA Banner -->
        <div class="relative w-full rounded-2xl overflow-hidden bg-[#13161e] border border-[#1e2535] min-h-[200px]">
          <!-- Radial gradient: dark center, transparent corners -->
          <div class="absolute inset-0 pointer-events-none z-[1]" style="background: radial-gradient(ellipse 60% 100% at center, #13161e 20%, transparent 75%)"></div>

          <!-- CTA Content -->
          <div class="relative z-10 flex flex-col items-center gap-5 py-10 px-8 sm:px-12">
            <!-- Text row -->
            <div class="flex flex-row items-center justify-center gap-5 w-full max-w-xl text-center">
              <div class="flex flex-col gap-1">
                <p class="text-[#22d3ee] font-semibold text-xs">{{ $t('SignUpGuide.ReadyToStart') }}</p>
                <p class="text-white font-semibold text-lg sm:text-xl leading-tight">{{ $t('SignUpGuide.UnlockPerks') }}</p>
              </div>
            </div>

            <!-- CTA Button -->
            <router-link
              to="/promotions"
              class="inline-block bg-[#c6f605] hover:bg-[#d4ff00] active:scale-95 transition-all text-black font-bold text-lg sm:text-xl px-10 py-4 rounded-xl text-center"
            >
              {{ $t('SignUpGuide.RegisterBtn') }}
            </router-link>

            <p class="text-gray-500 text-xs max-w-xl text-center" v-html="$t('SignUpGuide.EnsureCode')"></p>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
